import React, { useState } from "react";
import axios from "axios";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, ArrowLeft } from "lucide-react";
import { useCatalog } from "../context/CatalogContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { hero } = useCatalog();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const gradFrom = hero?.loginGradientFrom || "#2B1810";
  const gradTo = hero?.loginGradientTo || "#8B5A2B";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 6) return setError("Password must be at least 6 characters.");
    if (pw !== pw2) return setError("Passwords do not match.");
    if (!token) return setError("Reset link is missing. Please request a new one.");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password: pw });
      setDone(true);
      setTimeout(() => navigate("/auth", { replace: true }), 2200);
    } catch (err) {
      const detail =
        (err && err.response && err.response.data && err.response.data.detail) ||
        err.message ||
        "Could not update your password. Please try again.";
      setError(String(detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="auth-page"
      style={{
        "--auth-grad-from": gradFrom,
        "--auth-grad-to": gradTo,
      }}
    >
      <div className="auth-form-wrap auth-single">
        <div className="auth-form" data-testid="reset-password-form">
          <Link to="/auth" className="auth-back" data-testid="reset-back-to-login">
            <ArrowLeft size={16} /> Back to sign in
          </Link>
          <h2>Set a new password</h2>
          <p className="auth-hint">
            Choose a password you'll remember. It must be at least 6 characters.
          </p>

          {done ? (
            <div
              className="auth-ok"
              data-testid="reset-success"
              style={{
                marginTop: 12,
                padding: "14px 16px",
                borderRadius: 10,
                background: "rgba(139, 90, 43, 0.08)",
                color: "#4A3222",
                fontSize: 14,
              }}
            >
              Password updated. Taking you to the sign-in page…
            </div>
          ) : (
            <form onSubmit={submit}>
              <label className="auth-field">
                <span>New password</span>
                <div className="pw-wrap">
                  <input
                    type={showPw ? "text" : "password"}
                    required
                    autoComplete="new-password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="Min. 6 characters"
                    minLength={6}
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    className="pw-toggle"
                    onClick={() => setShowPw((s) => !s)}
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </label>
              <label className="auth-field">
                <span>Confirm new password</span>
                <input
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="new-password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  placeholder="Repeat your new password"
                  minLength={6}
                  data-testid="reset-password-confirm"
                />
              </label>
              {error && <div className="auth-error">{error}</div>}
              <button
                type="submit"
                className="btn btn-primary auth-submit"
                disabled={busy}
                data-testid="reset-submit-button"
              >
                {busy ? (
                  <>
                    <Loader2 size={16} className="spin" /> Updating…
                  </>
                ) : (
                  <>Update password</>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
